import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Grid } from '@mui/material';
import { CuriosityNamespaceType, CuriosityValue } from '@procureai/curiosity-ui-mui';
import {
  Widget,
  WidgetHeader,
  WidgetContent,
  WidgetIcon,
  DateFormatPreset,
  formatDate,
  useCurrency,
} from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { NULLISH_VALUE } from '~/common/constants';
import { I18nNamespace } from '~/common/types';
import { ClampedMultiline } from '~/components/ClampedMultiline/ClampedMultiline';
import { CuriosityBaseLink } from '~/components/CuriosityBaseLink/CuriosityBaseLink';
import { LabeledDataStack } from '~/components/LabeledDataStack/LabeledDataStack';

import { useSourcingProjectInformation } from './SourcingProjectInformationWidget.hooks';

export const SourcingProjectInformationWidget = () => {
  const { t: tHeader } = useTranslation(I18nNamespace.base, {
    keyPrefix: 'SourcingProjectInformationWidget',
  });
  const { t } = useTranslation(I18nNamespace.base, {
    keyPrefix: 'SourcingProjectInformationWidget.content',
  });
  const { t: tCommon } = useTranslation(undefined, {
    keyPrefix: 'Base.sourcingProject',
  });

  const { isLoading, sourcingProjectData } = useSourcingProjectInformation();
  const currencyConfig = sourcingProjectData.currency ? { code: sourcingProjectData.currency } : {};
  const { format } = useCurrency(currencyConfig);

  return (
    <Widget>
      <WidgetHeader title={tHeader('title')} icon={<WidgetIcon icon={<InfoOutlinedIcon fontSize="small" />} />} />
      <WidgetContent>
        <Grid size={12} container spacing={3}>
          <LabeledDataStack //
            size={12}
            loading={isLoading}
            label={t('title')}
            body={
              sourcingProjectData.title ? (
                <ClampedMultiline lines={4}>{sourcingProjectData.title}</ClampedMultiline>
              ) : (
                NULLISH_VALUE
              )
            }
          />
          <LabeledDataStack
            size={{ xs: 12, sm: 6 }}
            loading={isLoading}
            label={t('status')}
            body={sourcingProjectData.status ? tCommon(`status.${sourcingProjectData.status}`) : NULLISH_VALUE}
          />
          <LabeledDataStack
            size={{ xs: 12, sm: 6 }}
            loading={isLoading}
            label={t('startedAt')}
            body={
              sourcingProjectData.startedAt
                ? formatDate(DateFormatPreset.TWO_DIGIT, new Date(sourcingProjectData.startedAt))
                : NULLISH_VALUE
            }
          />
          <LabeledDataStack
            size={{ xs: 12, sm: 6 }}
            loading={isLoading}
            label={t('variant')}
            body={sourcingProjectData.variant ? tCommon(`variant.${sourcingProjectData.variant}`) : NULLISH_VALUE}
          />
          <LabeledDataStack
            size={{ xs: 12, sm: 6 }}
            loading={isLoading}
            label={t('estimatedTotalValue')}
            body={
              sourcingProjectData.estimatedTotalValue //
                ? format(sourcingProjectData.estimatedTotalValue)
                : NULLISH_VALUE
            }
          />
          <LabeledDataStack
            size={12}
            loading={isLoading}
            label={t('responsibleBuyer')}
            body={
              sourcingProjectData.buyerId ? (
                <CuriosityValue
                  ns={CuriosityNamespaceType.INTERNAL_USER}
                  i18nKey={sourcingProjectData.buyerId}
                  fallback={NULLISH_VALUE}
                />
              ) : (
                NULLISH_VALUE
              )
            }
          />
          <LabeledDataStack
            size={12}
            loading={isLoading}
            label={t('commodityGroup')}
            body={
              sourcingProjectData.commodityGroupId ? (
                <CuriosityBaseLink
                  ns={CuriosityNamespaceType.COMMODITY_GROUP}
                  i18nKey={sourcingProjectData.commodityGroupId}
                  fallback={NULLISH_VALUE}
                />
              ) : (
                NULLISH_VALUE
              )
            }
          />
        </Grid>
      </WidgetContent>
    </Widget>
  );
};
